import { DateTime } from "luxon";

type SortFunc<T> = (v1: T, v2: T) => number;
type Defined<V> = Exclude<V, null | undefined>;
type Picker<T, V> = (row: T) => V | null | undefined;

function nullAwareSort<T, V>(
  extract: Picker<T, V>,
  compare: SortFunc<Defined<V>>,
  nullsFirst?: boolean,
): SortFunc<T> {
  return (r1, r2) => {
    const v1 = extract(r1);
    const v2 = extract(r2);

    if ((v1 === null || v1 === undefined) && (v2 === null || v2 === undefined)) {
      return 0;
    } else if (v1 === null || v1 === undefined) {
      return nullsFirst ? -1 : 1;
    } else if (v2 === null || v2 === undefined) {
      return nullsFirst ? 1 : -1;
    }

    return compare(v1 as Defined<V>, v2 as Defined<V>);
  };
}

export function stringSort<T>(extract: Picker<T, string>, nullsFirst = true): SortFunc<T> {
  return nullAwareSort(extract, (v1, v2) => v1.localeCompare(v2), nullsFirst);
}

export function numberSort<T>(
  extract: (value: T) => number | null | undefined,
  sortDirection?: "ascend" | "descend" | null | undefined, // sort direction is needed to sort nulls properly
): SortFunc<T> {
  return nullAwareSort(extract, (v1, v2) => v1 - v2, sortDirection === "descend");
}

type DateLike = string | Date | DateTime;

/**
 * Creates a sort function for date values that can handle multiple date formats
 * @param extract Function to extract the date value from the row
 * @param nullsFirst Whether null values should be sorted first (default: true)
 * @returns A sort function that can handle various date formats
 */
export function dateSort<T>(extract: Picker<T, DateLike>, nullsFirst = true): SortFunc<T> {
  return nullAwareSort(
    extract,
    (v1, v2) => {
      const date1 = parseDateValue(v1);
      const date2 = parseDateValue(v2);

      if (!date1.isValid) return -1;
      if (!date2.isValid) return 1;

      return date1.toMillis() - date2.toMillis();
    },
    nullsFirst,
  );
}

/**
 * Parses a date-like value into a Luxon DateTime object
 * Supports ISO strings, SQL dates, JS Date objects, and Luxon DateTime objects
 * This is mostly usd because the closeDate field is sometimes a string and sometimes a date
 */
export function parseDateValue(value: DateLike): DateTime {
  if (value instanceof DateTime) {
    return value;
  }

  if (value instanceof Date) {
    return DateTime.fromJSDate(value, { zone: "utc" });
  }

  if (typeof value === "string") {
    // Try ISO format first
    const isoDate = DateTime.fromISO(value, { zone: "utc" });
    if (isoDate.isValid) {
      return isoDate;
    }

    // Try SQL format as fallback
    return DateTime.fromSQL(value, { zone: "utc" });
  }

  console.warn(`Invalid date format: ${value}`);
  return DateTime.invalid("Invalid date format");
}
